import { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FormFields,
  IconLibrary,
  Layout,
  PageSection,
  StackablePageButtons,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

function WorkflowEdit() {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/workflows/${pageId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/workflows/${pageId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);

  const preProcessRequestData = data => {
    const {
      isActive,
      createdByUserId,
      lastModifiedByUserId,
      id,
      form,
      ...other
    } = data;
    return {
      isActive: isActive === 'true',
      ...other,
      formId: data.form.value,
    };
  };

  const preProcessFetchData = data => {
    const { form, ...other } = data;
    return {
      ...other,
      form: {
        label: form?.formName,
        value: form?.id,
      },
    };
  };

  const submitEditForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'WORKFLOW_UPDATE_SUCCESS',
          defaultMessage: 'Workflow was saved successfully',
        }),
      );
      setInitialValues(preProcessFetchData(response?.data));
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'WORKFLOW_UPDATE_FAILURE',
          defaultMessage: 'Error: Could not save the workflow',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    setFormErrors(null);
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(response?.data),
      });
      setLoading(false);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'WORKFLOW_DATA_FETCH_FAILURE',
          defaultMessage: 'Error: Could not fetch the workflow data',
        }),
      });
    }
  };

  useEffect(() => {
    fetchInitialData(pageId);
  }, [pageId]);

  const formProps = {
    customComponents: FormFields,
    schema: editSchema,
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'WORKFLOWS_BREADCRUMB',
        defaultMessage: 'Workflows',
      }),
      icon: IconLibrary.Workflows,
      onClick: () => {
        navigate('/workflows');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_WORKFLOW_EDIT_BREADCRUMB',
        defaultMessage: 'Edit Workflow',
      }),
      icon: IconLibrary.Workflow,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_WORKFLOWS_STACKABLE_BUTTON',
        defaultMessage: 'Create Workflow',
      }),
      permissionName: 'CAN_CREATE_WORKFLOW',
      Icon: IconLibrary.Workflow,
      variant: 'outlined',
      onClick: () => navigate('/workflows/create'),
    },
    {
      label: formatMessage({
        id: 'EDIT_WORKFLOWS_STACKABLE_BUTTON',
        defaultMessage: 'List Workflows',
      }),
      Icon: IconLibrary.Workflows,
      variant: 'outlined',
      onClick: () => navigate('/workflows'),
    },
  ];

  return (
    <Layout>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing..." />
        </LoadingEvent>
      )}

      {!loading && (
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
      )}

      {!loading && (
        <PageSection
          label={formatMessage({
            id: 'WORKFLOW_EDIT_HEADING',
            defaultMessage: 'Edit Workflow',
          })}>
          <Box>
            <CRUDComponent
              errors={formErrors}
              type={CRUD_COMPONENT_TYPE.FORM}
              formProps={formProps}>
              <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
                <Button
                  variant="contained"
                  startIcon={<IconLibrary.Save />}
                  color="primary"
                  onClick={() => editFormRef.current?.submit()}>
                  {formatMessage({
                    id: 'WORKFLOW_UPDATE_SAVE_BUTTON',
                    defaultMessage: 'Save Workflow',
                  })}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => editFormRef.current?.reset()}>
                  {formatMessage({
                    id: 'WORKFLOW_UPDATE_RESET_BUTTON',
                    defaultMessage: 'Reset',
                  })}
                </Button>
              </Box>
            </CRUDComponent>
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(WorkflowEdit);
